import type { ReactNode } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { rotaVisivelPorAtividade } from '@triade/contracts';
import { useAtividadesDaEmpresa } from '@/api/atividades';
import { useCompras, useFluxo, useNecessidade, useResumoMes } from '@/api/plano';
import { usePode } from '@/api/me';
import { ErroLista } from '@/components/cargos';
import { formatarContagem, formatarData, formatarDecimal, formatarMoeda } from '@/lib/format';
import { cn } from '@/lib/utils';
import { deslocarMes, rotuloMes } from './carteira-mes';
import { periodoPadraoFluxo } from './fluxo-busca';

const BOTAO_MES = 'flex size-[34px] items-center justify-center rounded-[9px] border border-border bg-card hover:bg-line-soft';

type StatusPasso = 'Em dia' | 'Vazio' | 'Atenção' | 'Carregando…';

const CLASSE_STATUS_PASSO: Record<StatusPasso, string> = {
  'Em dia': 'bg-success-bg text-success-ink',
  Vazio: 'bg-surface-muted text-ink-muted',
  Atenção: 'bg-warning-bg text-warning-ink',
  'Carregando…': 'bg-line-soft text-ink-secondary',
};

/**
 * Plano do mês (/plano): os passos do mes, cada um com o que e, o status e 1-2 numeros vindos da API.
 * Nenhum valor e calculado aqui: R$, custo e resultado chegam prontos (ausentes = "—"). Cada passo so monta
 * (e so chama a API) se o usuario tem a permissao da tela de origem e se a atividade da empresa mostra a tela.
 * O numero do cartao e a posicao entre os passos visiveis (sem buraco).
 */
export function PlanoMesPage({ mes }: { mes: string }) {
  const navigate = useNavigate();
  const { pode } = usePode();
  const atividades = useAtividadesDaEmpresa();
  const passos = [
    { rota: '/plano/carteira', permitido: pode('carteira.ver'), Componente: PassoCarteira },
    { rota: '/plano/necessidade', permitido: pode('plano.ver'), Componente: PassoNecessidade },
    { rota: '/plano/compras', permitido: pode('plano.ver'), Componente: PassoCompras },
    { rota: '/plano/custo-fabrica', permitido: pode('carteira.ver') && pode('plano.ver'), Componente: PassoCustoFabrica },
    { rota: '/plano/fluxo', permitido: pode('plano.ver'), Componente: PassoFluxo },
  ].filter((p) => p.permitido && rotaVisivelPorAtividade(p.rota, atividades));
  const mudar = (novo: string) => void navigate({ to: '/plano', search: { mes: novo }, replace: true });

  return (
    <div className="mx-auto flex w-full max-w-[1280px] flex-col gap-5 pb-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-small text-ink-muted">
            Plano <span aria-hidden="true">/</span> Plano do mês
          </div>
          <h1 className="mt-1 text-display font-semibold tracking-[-0.01em]">Plano do mês</h1>
          <p className="text-small text-ink-secondary">Os cinco passos do mês, na ordem em que um alimenta o outro</p>
        </div>
        <nav aria-label="Mês do plano" className="flex items-center gap-2">
          <button type="button" aria-label="Mês anterior" className={BOTAO_MES} onClick={() => mudar(deslocarMes(mes, -1))}>
            <ChevronLeft aria-hidden="true" className="size-4" />
          </button>
          <span aria-live="polite" className="min-w-[90px] text-center text-body font-semibold tabular-nums" data-testid="mes-plano">
            {rotuloMes(mes)}
          </span>
          <button type="button" aria-label="Próximo mês" className={BOTAO_MES} onClick={() => mudar(deslocarMes(mes, 1))}>
            <ChevronRight aria-hidden="true" className="size-4" />
          </button>
        </nav>
      </div>

      <ol aria-label="Passos do plano do mês" className="flex flex-col gap-3">
        {passos.map(({ rota, Componente }, i) => (
          <Componente key={rota} mes={mes} numero={i + 1} />
        ))}
      </ol>
    </div>
  );
}

// ---------- Passos ----------

function PassoCarteira({ mes, numero }: PassoProps) {
  const resumo = useResumoMes(mes);
  const base = {
    numero,
    titulo: 'Carteira',
    rota: '/plano/carteira' as const,
    oQue: 'Os pedidos de venda do mês. Cada pedido gera a necessidade de matéria-prima.',
  };

  if (resumo.isError && !resumo.data) return <CartaoPasso {...base} status="Atenção" erro={resumo.error} tituloErro="Não foi possível carregar a carteira" onTentar={() => void resumo.refetch()} />;
  if (!resumo.data) return <CartaoPasso {...base} status="Carregando…" />;

  const r = resumo.data;
  const pedidos = r.porStatus.reduce((soma, linha) => soma + linha.pedidos, 0);
  if (pedidos === 0) {
    return (
      <CartaoPasso {...base} status="Vazio">
        Nenhum pedido neste mês. Gere pedidos a partir de orçamentos aprovados ou lance um na Carteira.
      </CartaoPasso>
    );
  }
  return (
    <CartaoPasso {...base} status="Em dia">
      <Numeros
        itens={[
          [pedidos === 1 ? 'Pedido' : 'Pedidos', formatarContagem(pedidos)],
          ['Toneladas', `${formatarDecimal(r.quantidade, 0)} t`],
          ...(r.faturamento !== undefined ? ([['Faturamento', formatarMoeda(r.faturamento)]] as Array<[string, string]>) : []),
        ]}
      />
    </CartaoPasso>
  );
}

function PassoNecessidade({ mes, numero }: PassoProps) {
  const necessidade = useNecessidade({ de: `${mes}-01`, ate: ultimoDiaDoMes(mes), incluirPendente: false });
  const base = {
    numero,
    titulo: 'Necessidade de MP',
    rota: '/plano/necessidade' as const,
    oQue: 'Quanto de matéria-prima os pedidos exigem, e o que ainda falta comprar.',
  };

  if (necessidade.isError && !necessidade.data) {
    return <CartaoPasso {...base} status="Atenção" erro={necessidade.error} tituloErro="Não foi possível carregar a necessidade" onTentar={() => void necessidade.refetch()} />;
  }
  if (!necessidade.data) return <CartaoPasso {...base} status="Carregando…" />;

  const { itens, semFicha } = necessidade.data;
  const contagem = (
    <Numeros itens={[[itens.length === 1 ? 'Item de MP' : 'Itens de MP', formatarContagem(itens.length)]]} />
  );

  if (semFicha.length > 0) {
    const n = semFicha.length;
    return (
      <CartaoPasso {...base} status="Atenção">
        {contagem}
        <p role="note" className="mt-2 text-caption text-warning-ink">
          {`⚠ ${formatarContagem(n)} ${n === 1 ? 'pedido fora da conta (sem ficha)' : 'pedidos fora da conta (sem ficha)'}`}
        </p>
      </CartaoPasso>
    );
  }
  if (itens.length === 0) {
    return (
      <CartaoPasso {...base} status="Vazio">
        Sem necessidade: nenhum pedido do mês usa ficha com MP.
      </CartaoPasso>
    );
  }
  return <CartaoPasso {...base} status="Em dia">{contagem}</CartaoPasso>;
}

function PassoCompras({ numero }: PassoProps) {
  // A API de compras nao filtra por mes: estes numeros sao de todas as compras lancadas.
  const compras = useCompras({});
  const base = {
    numero,
    titulo: 'Compras de MP',
    rota: '/plano/compras' as const,
    oQue: 'As compras de matéria-prima lançadas, para acompanhar o que já está comprado.',
  };

  if (compras.isError && !compras.data) return <CartaoPasso {...base} status="Atenção" erro={compras.error} tituloErro="Não foi possível carregar as compras" onTentar={() => void compras.refetch()} />;
  if (!compras.data) return <CartaoPasso {...base} status="Carregando…" />;

  const c = compras.data;
  if (c.total === 0) {
    return (
      <CartaoPasso {...base} status="Vazio">
        Nenhuma compra lançada.
      </CartaoPasso>
    );
  }
  return (
    <CartaoPasso {...base} status="Em dia">
      <Numeros
        itens={[
          ['Compras lançadas no total', formatarContagem(c.total)],
          ['Valor lançado no total', formatarMoeda(c.somaTotal)],
        ]}
      />
    </CartaoPasso>
  );
}

function PassoCustoFabrica({ mes, numero }: PassoProps) {
  const resumo = useResumoMes(mes);
  const base = {
    numero,
    titulo: 'Custo fábrica',
    rota: '/plano/custo-fabrica' as const,
    oQue: 'O custo de fabricação previsto do mês e o resultado que sobra dele.',
  };

  if (resumo.isError && !resumo.data) return <CartaoPasso {...base} status="Atenção" erro={resumo.error} tituloErro="Não foi possível carregar o custo fábrica" onTentar={() => void resumo.refetch()} />;
  if (!resumo.data) return <CartaoPasso {...base} status="Carregando…" />;

  const r = resumo.data;
  // So decide o rotulo (Vazio x Em dia); o valor exibido e o que a API mandou.
  const vazio = r.custoFabrica === undefined || /^-?0+(\.0+)?$/.test(r.custoFabrica);
  return (
    <CartaoPasso {...base} status={vazio ? 'Vazio' : 'Em dia'}>
      <Numeros
        itens={[
          ['Custo fábrica', formatarMoeda(r.custoFabrica)],
          ['Resultado gerencial', formatarMoeda(r.resultadoGerencial)],
        ]}
      />
      {vazio && <p className="mt-2 text-caption text-ink-muted">Nenhuma rubrica lançada neste mês. Lance o previsto em Custo fábrica.</p>}
    </CartaoPasso>
  );
}

function PassoFluxo({ mes, numero }: PassoProps) {
  const fluxo = useFluxo({ ...periodoPadraoFluxo(mes), granularidade: 'semana' });
  const base = {
    numero,
    titulo: 'Fluxo de caixa',
    rota: '/plano/fluxo' as const,
    oQue: 'O saldo de caixa projetado, a partir das vendas, das compras e das rubricas.',
  };

  if (fluxo.isError && !fluxo.data) return <CartaoPasso {...base} status="Atenção" erro={fluxo.error} tituloErro="Não foi possível carregar o fluxo de caixa" onTentar={() => void fluxo.refetch()} />;
  if (!fluxo.data) return <CartaoPasso {...base} status="Carregando…" />;

  const negativo = fluxo.data.primeiroNegativo;
  if (negativo) {
    return (
      <CartaoPasso {...base} status="Atenção">
        Saldo negativo a partir de {formatarData(negativo)}
      </CartaoPasso>
    );
  }
  return (
    <CartaoPasso {...base} status="Em dia">
      Nenhum período com saldo negativo
    </CartaoPasso>
  );
}

// ---------- Comuns ----------

/** Props de cada passo: o mes do plano e o numero do cartao (posicao entre os passos montados). */
interface PassoProps {
  mes: string;
  numero: number;
}

/** Ultimo dia do mes AAAA-MM, em AAAA-MM-DD (calendario, via Date.UTC). */
function ultimoDiaDoMes(mes: string): string {
  const [ano, m] = mes.split('-').map(Number) as [number, number];
  const dia = new Date(Date.UTC(ano, m, 0)).getUTCDate();
  return `${mes}-${String(dia).padStart(2, '0')}`;
}

interface CartaoPassoProps {
  numero: number;
  titulo: string;
  rota: '/plano/carteira' | '/plano/necessidade' | '/plano/compras' | '/plano/custo-fabrica' | '/plano/fluxo';
  oQue: string;
  status: StatusPasso;
  children?: ReactNode;
  erro?: unknown;
  tituloErro?: string;
  onTentar?: () => void;
}

function CartaoPasso({ numero, titulo, rota, oQue, status, children, erro, tituloErro = '', onTentar }: CartaoPassoProps) {
  return (
    <li data-testid={`passo-${numero}`} className="rounded-2xl border border-border bg-card p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span aria-hidden="true" className="flex size-7 shrink-0 items-center justify-center rounded-full bg-surface-muted text-caption font-semibold tabular-nums">
            {numero}
          </span>
          <div>
            <h2 className="text-body font-semibold">
              <Link to={rota} className="hover:underline">
                {titulo}
              </Link>
            </h2>
            <p className="text-small text-ink-secondary">{oQue}</p>
          </div>
        </div>
        <span className={cn('inline-block rounded-full px-2 py-0.5 text-caption font-semibold whitespace-nowrap', CLASSE_STATUS_PASSO[status])}>{status}</span>
      </div>
      {(erro !== undefined || children !== undefined) && (
        <div className="mt-3 pl-10 text-small text-ink-body">
          {erro !== undefined ? <ErroLista erro={erro} onTentar={onTentar} titulo={tituloErro} /> : children}
        </div>
      )}
    </li>
  );
}

function Numeros({ itens }: { itens: Array<[string, string]> }) {
  return (
    <dl className="flex flex-wrap gap-x-6 gap-y-1">
      {itens.map(([rotulo, valor]) => (
        <div key={rotulo} className="flex items-baseline gap-1.5">
          <dt className="text-caption text-ink-muted">{rotulo}</dt>
          <dd className="font-semibold tabular-nums">{valor}</dd>
        </div>
      ))}
    </dl>
  );
}
